import { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowRightIcon, StarIcon } from 'lucide-react';
import { MediaView } from '../shared/MediaView';
import { HeroDoors } from './HeroDoors';
import { founder, pressLogos } from '../../data/content';
import { policyCopy } from '../../data/policies';
import { usePages } from '../../contexts/SiteContext';
import { useOverlayActions } from '../../contexts/StoreContext';
import { ease } from '../../core/format/motion';
import { plural } from '../../core/format/text';
import { mediaOr } from '../../core/rules/media';
import { fill } from '../../utils/copy';
type Props = {
  reviewCount: number;
  avgRating: string;
};

/** Wanneer de kopregels beginnen, en hoeveel later elke volgende. */
const LINE_DELAY_S = 0.08;
const LINE_STEP_S = 0.07;

/**
 * De opening (docs/VORMGEVING.md §3). Eén boodschap: salon én thuis, door één specialist.
 *
 * Computer: links de kop, de uitleg en één hoofdknop (het huidadvies, dat naar salon of shop
 * leidt); onderaan twee "deuren" naar de salon en de webshop. Rechts het beeld tot de rand,
 * met een onderschrift ín de foto: wie je ziet, en waarom je haar kunt vertrouwen. Zo werkt het
 * met elke foto uit de shoot, en staat er nooit tekst los op een drukke achtergrond.
 *
 * Telefoon: het beeld bovenaan (40% van het scherm), daaronder kop, uitleg en knop: alles in
 * het eerste scherm. De deuren staan direct daaronder.
 *
 * Beweging: alleen de kopregels schuiven uit een masker, de rest komt zacht omhoog. Het beeld
 * zelf animeert niet bij binnenkomst (het is het grootste element: zo snel mogelijk zichtbaar);
 * bij scrollen schuift het een fractie mee. Alles uit bij "minder beweging".
 */
export function HomeHero({
  reviewCount,
  avgRating
}: Props) {
  const pages = usePages();
  const ref = useRef<HTMLElement>(null);
  const {
    openQuiz
  } = useOverlayActions();
  const reduce = useReducedMotion();
  const copy = pages.home.hero;
  const media = mediaOr(copy.media, {
    image: founder.portrait,
    alt: `${founder.name}, huidspecialist`,
    focus: {
      x: 50,
      y: 25
    }
  });
  const press = pressLogos[0]?.name;
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });
  const drift = useTransform(scrollYProgress, [0, 1], ['0%', '7%']);
  const afterLines = LINE_DELAY_S + copy.lines.length * LINE_STEP_S;

  /** Zacht omhoog, na `delay` seconden. */
  const rise = (delay: number) => reduce ? {} : {
    initial: {
      opacity: 0,
      y: 8
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.26,
      delay,
      ease
    }
  };
  return <section ref={ref} className="bg-sand" aria-label="Introductie" data-id="element-7350" data-mp="12739z8ugy5jz">
      <div className="grid lg:min-h-[min(calc(100svh-120px),54rem)] lg:grid-cols-2" data-id="element-7351" data-mp="127v5kjo2g3a9">
        {/* Beeld: op de telefoon eerst, op de computer rechts tot de rand. */}
        <div className="relative h-[40svh] min-h-[300px] max-h-[460px] overflow-hidden bg-blush sm:h-[52svh] sm:max-h-[560px] lg:order-2 lg:h-auto lg:max-h-none" data-id="element-7352" data-mp="1c6anbw17e073">
          {/* 8% extra hoogte boven: het beeld zakt bij scrollen iets trager weg dan de pagina, zonder ooit een rand te tonen. */}
          <motion.div style={reduce ? undefined : {
          y: drift
        }} className="absolute inset-x-0 -top-[8%] bottom-0" data-id="element-7353" data-mp="2wn1y0k7aajhk">
            <MediaView media={media} priority sizes="(min-width: 1024px) 50vw, 100vw" controlsPosition="top-right" className="h-full w-full" data-id="element-7354" data-mp="2rp07je7j3hw2" />
          </motion.div>

          {/* Het onderschrift ín de foto: op een eigen shell-vlak, dus leesbaar op elk beeld. */}
          <motion.div {...rise(0.25)} className="absolute bottom-4 left-4 max-w-[calc(100%-2rem)] bg-shell px-4 py-3 sm:bottom-6 sm:left-6 sm:px-5 sm:py-4 lg:bottom-10 lg:left-10" data-id="element-7355" data-mp="3fd9mpxa8l7e8">
            <p data-id="element-7356" data-mp="35oro9mpfjcdr">
              <span className="block font-display text-[1.15rem] leading-tight text-ink sm:text-[1.3rem]" data-id="element-7357" data-mp="35n6x872bqbkn">{founder.name}</span>
              <span className="mt-0.5 block text-[13px] leading-snug text-muted" data-id="element-7358" data-mp="702qminwa5wt">
                {founder.role} · {policyCopy.experience}
              </span>
              {(reviewCount > 0 || press) && <span className="mt-2.5 hidden items-center gap-3 border-t border-line pt-2.5 text-[13px] text-ink sm:flex" data-id="element-7359" data-mp="3ix0d7sdrpowr">
                  {reviewCount > 0 && <span className="inline-flex items-center gap-1.5 whitespace-nowrap" data-id="element-7360" data-mp="38y1305dctl7n">
                      <StarIcon className="h-3.5 w-3.5 fill-current text-clay" strokeWidth={0} aria-hidden="true" data-id="element-7361" data-mp="3dwcf6mr4g7t1" />
                      {avgRating}
                      <span className="sr-only" data-id="element-7362" data-mp="aeugb4tbub4r"> van 5 sterren,</span>
                      <span className="text-muted" data-id="element-7363" data-mp="ae161tv5t3hv"> · {plural(reviewCount, 'review', 'reviews')}</span>
                    </span>}
                  {press && <span className="whitespace-nowrap text-muted" data-id="element-7364" data-mp="o99wv7ew2ba">Aanbevolen door {press}</span>}
                </span>}
            </p>
          </motion.div>
        </div>

        {/* Tekst */}
        <div className="flex flex-col px-6 pb-12 pt-7 sm:px-10 sm:pt-10 lg:order-1 lg:py-14 lg:pl-[max(2.5rem,calc((100vw-80rem)/2+2.5rem))] lg:pr-16 xl:pr-24" data-id="element-7365" data-mp="t6djbbncpp6s">
          <div className="lg:my-auto" data-id="element-7366" data-mp="28wnviruczm2e">
            <motion.h1 {...rise(0)} className="text-[14px] text-clay" data-id="element-7367" data-mp="28vl9ax7ze7st">
              {fill(copy.eyebrow)}
            </motion.h1>
            <p className="mt-4 text-balance font-display text-[2.2rem] font-light leading-[1.04] tracking-[-0.02em] text-ink sm:text-[3rem] lg:mt-5 lg:text-[clamp(2.75rem,3.5vw,3.9rem)]" data-id="element-7368" data-mp="2mn9kmyaeos8q">
              {copy.lines.map((line, i) => <span key={`${i}-${line.text}`} className="block overflow-hidden pb-[0.06em]" data-id="element-7369" data-mp="1py1vlgakw102" data-mpi={i}>
                  <motion.span className={`block ${line.accent ? 'italic text-clay' : ''}`} initial={reduce ? false : {
                y: '105%'
              }} animate={{
                y: 0
              }} transition={{
                duration: 0.3,
                delay: LINE_DELAY_S + i * LINE_STEP_S,
                ease
              }} data-id="element-7370" data-mp="23xbbeeuvgt3i">
                    {fill(line.text)}
                  </motion.span>
                </span>)}
            </p>
            {copy.body && <motion.p {...rise(afterLines)} className="mt-5 max-w-[34rem] text-[16px] leading-relaxed text-muted sm:text-[17px] lg:mt-6" data-id="element-7371" data-mp="282jzg3j3x121">
                {fill(copy.body)}
              </motion.p>}
            {copy.cta && <motion.div {...rise(afterLines + 0.05)} className="mt-7 lg:mt-9" data-id="element-7372" data-mp="2m08n1j4k6mas">
                <button type="button" onClick={openQuiz} aria-haspopup="dialog" className="group inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-full bg-ink px-7 text-[15px] text-shell transition-[background-color,transform] duration-150 ease-soft hover:bg-clay active:scale-[0.98] sm:w-auto" data-id="element-7373" data-mp="1p9zjtnsf4sg6">
                  {fill(copy.cta)}
                  <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-7374" data-mp="15l7c23op6a5t" />
                </button>
              </motion.div>}
          </div>

          <motion.div {...rise(afterLines + 0.1)} className="mt-10 lg:mt-12" data-id="element-7375" data-mp="y2ha2x87thgj">
            <HeroDoors data-id="element-7376" data-mp="y0emejujc0ez" />
          </motion.div>
        </div>
      </div>
    </section>;
}